import { useState, useCallback, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { AdvertiserForm } from '@/components/admin/AdvertiserForm';
import { AdvertiserList } from '@/components/admin/AdvertiserList';
import * as advertiserApi from '@/api/advertisers';

interface Advertiser {
  id: number;
  name: string;
  address: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  campaignCount?: number;
  clientId?: number | null;
  clientName?: string | null;
}

export default function AdminAdvertisersPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] = useState<Advertiser | null>(null);
  const queryClient = useQueryClient();

  // Fetch advertisers
  const {
    data: advertisersData = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['advertisers', debouncedSearchTerm],
    queryFn: () => advertiserApi.fetchAdvertisers(debouncedSearchTerm),
  });

  // Handle create
  const handleCreate = useCallback(
    async (formData: {
      name: string;
      address?: string;
      contactName?: string;
      contactEmail?: string;
      contactPhone?: string;
      clientId?: number | null;
    }) => {
      try {
        await advertiserApi.createAdvertiser(formData);
        setIsCreateDialogOpen(false);
        queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      } catch (error: any) {
        throw error;
      }
    },
    [queryClient]
  );

  // Handle update
  const handleUpdate = useCallback(
    async (formData: {
      name: string;
      address?: string;
      contactName?: string;
      contactEmail?: string;
      contactPhone?: string;
      clientId?: number | null;
    }) => {
      if (!editingAdvertiser?.id) return;

      try {
        await advertiserApi.updateAdvertiser(editingAdvertiser.id, formData);
        setEditingAdvertiser(null);
        queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      } catch (error: any) {
        throw error;
      }
    },
    [editingAdvertiser?.id, queryClient]
  );

  // Handle delete
  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await advertiserApi.deleteAdvertiser(id);
        queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      } catch (error: any) {
        throw error;
      }
    },
    [queryClient]
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={Building2}
        title="Advertisers"
        description="Manage advertiser information and link campaigns"
      >
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Advertiser
        </Button>
      </PageHeader>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          Failed to load advertisers. Please try again.
        </div>
      )}

      {/* Advertiser List */}
      <AdvertiserList
        advertisers={advertisersData}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onEdit={setEditingAdvertiser}
        onDelete={handleDelete}
      />

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Advertiser</DialogTitle>
            <DialogDescription>
              Add a new advertiser to the system. You can link campaigns to it later.
            </DialogDescription>
          </DialogHeader>
          <AdvertiserForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editingAdvertiser !== null} onOpenChange={(open) => !open && setEditingAdvertiser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Advertiser</DialogTitle>
            <DialogDescription>
              Update the advertiser details below.
            </DialogDescription>
          </DialogHeader>
          {editingAdvertiser && (
            <AdvertiserForm
              initialData={editingAdvertiser}
              onSubmit={handleUpdate}
              onCancel={() => setEditingAdvertiser(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
